import React from "react"
import { gql } from "@/generated"
import { useCurrentOrganization } from "@/novus/hooks/useCurrentOrganization"
import invariant from "tiny-invariant"
import { z, ZodType } from "zod"
import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useSafeMutation } from "@/util/useSafeMutation"
import { useToast } from "@/components/ui/use-toast"
import { logEvent } from "@/analytics/analytics"
import { displayValidationErrors } from "@/util/validations"
import { FieldGroup } from "@/components/ui/field-group"
import { RhfField } from "@/components/ui/rhf-field"
import { FormActions } from "@/components/ui/form-actions"
import { Button } from "@/components/ui/button"

const VerifyMembershipMutation = gql(`
  mutation VerifyOrganizationMembership($input: VerifyOrganizationMembershipInput!){
    verifyOrganizationMembership(input: $input) {
      organizationMembership {
        ...OrganizationMembership
      }
      errors {
        path
        fullMessages
      }
    }
  }
`)

type FormValues = {
  magicCode: string
}

const schema: ZodType<FormValues> = z.object({
  magicCode: z.string().min(1, "Code is required"),
})

export const MagicCodeForm = ({ onBackClick }: { onBackClick: () => void }) => {
  const { membership } = useCurrentOrganization()
  const [execVerifyMembership] = useSafeMutation(VerifyMembershipMutation)
  const { toast } = useToast()

  invariant(membership)

  const defaultValues: FormValues = {
    magicCode: "",
  }

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues,
  })

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    logEvent("Submit Magic Code")

    try {
      const { data } = await execVerifyMembership({
        variables: {
          input: {
            organizationMembershipId: membership.id,
            magicCode: values.magicCode,
          },
        },
      })

      if (
        data?.verifyOrganizationMembership.errors &&
        data?.verifyOrganizationMembership.errors.length > 0
      ) {
        throw data?.verifyOrganizationMembership.errors
      }

      toast({ title: "Thank you, your email has been verified." })
      // at this point, the RequireUserNotVerified layout should trigger and the user will be redirected.
    } catch (e: any) {
      console.error(e)
      displayValidationErrors({
        recordType: "organizationMembership",
        errors: e,
        setError: form.setError,
        toast,
        validationErrorTitle: "Code could not be verified",
      })
    }
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FieldGroup>
          <RhfField
            name="magicCode"
            control={form.control}
            placeholder="Code..."
            label="Please enter the 6 digit verification code"
          />
        </FieldGroup>
        <FormActions>
          <Button
            type="submit"
            variant="default"
            disabled={form.formState.isSubmitting}
          >
            Verify Code
          </Button>
        </FormActions>
      </form>
      <div className="text-center mt-3">
        <Button variant="link" size="md" onClick={onBackClick}>
          Return to previous step
        </Button>
      </div>
    </FormProvider>
  )
}
